import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { CommercialBanner } from "@/components/billing/Commercial";
import { Bell, Building2, Receipt, ClipboardList, Clock, FileStack, LayoutDashboard, LogOut, Menu, PenLine, Phone, ShieldCheck, SlidersHorizontal, UserRound } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import ClientShell, { useTheme } from "@/components/ClientShell";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitch, useLang } from "@/i18n";
import { BRAND } from "@/brand";
import { api, fmtDateTime, ROLE_LABEL } from "@/lib/api";
import { Wallet } from "lucide-react";

const NAVC = [
  ["Queues", [["/", "Today", LayoutDashboard, ["/"]], ["/waiting", "Waiting on client", Clock, ["/waiting"]], ["/quotes", "Quote desk", PenLine, ["/quotes"]], ["/billing", "Payments", Receipt, ["/billing"]]]],
  ["Workspace", [["/tasks", "Tasks", ClipboardList, ["/tasks"]], ["/leads", "Leads", Phone, ["/leads"]], ["/clients", "Clients", Building2, ["/clients", "/companies"]], ["/finance", "Finance", Wallet, ["/finance"]], ["/petty-cash", "Petty cash", Wallet, ["/petty-cash"]], ["/library", "Library", FileStack, ["/library"]], ["/me", "My Workspace", UserRound, ["/me", "/performance"]], ["/admin", "Admin", SlidersHorizontal, ["/admin"]]]],
];
const STAFF_PERM = { "/library": (u) => ["templates", "templates_archive", "templates_delete", "settings", "holidays_manage"].some((k) => u.perms?.[k]), "/billing": (u) => !!(u.perms?.billing_admin || u.perms?.payments_verify), "/leads": (u) => !!u.perms?.leads_view, "/admin": (u) => !!(u.perms?.users_admin || u.perms?.audit || u.perms?.roles_manage || u.perms?.staff_perf) };
const staffOk = (u) => ([to]) => (to === "/finance" ? !!u.fin : to === "/petty-cash" ? !!u.is_keeper : u.role === "admin" || !STAFF_PERM[to] || STAFF_PERM[to](u));
const onPath = (pathname, pre) => pre.some((p) => (p === "/" ? pathname === "/" : pathname === p || pathname.startsWith(`${p}/`)));
const slug = (l) => l.toLowerCase().replace(/ /g, "-");

function StaffNav({ user, onClick, badges }) {
  const { t } = useLang();
  const { pathname } = useLocation();
  return (
    <nav data-testid="staff-sidebar-nav">
      {NAVC.map(([g, items]) => (
        <div key={g} data-testid={`nav-group-${slug(g)}`}>
          <div className="px-3 pb-[5px] pt-[14px] text-[10.5px] uppercase tracking-[.12em] text-[#5f6f7d]">{t(g)}</div>
          {items.filter(staffOk(user)).map(([to, label, Icon, pre]) => {
            const on = onPath(pathname, pre);
            return (
              <NavLink key={to} to={to} onClick={onClick} data-testid={`nav-${slug(label)}`} className={`flex items-center gap-[11px] rounded-[10px] px-3 py-[9px] text-[14.5px] font-medium transition-colors ${on ? "bg-[rgba(0,163,224,.16)] text-white [&>svg]:text-[#38BDF8]" : "text-[#B8C4CF] hover:bg-white/[.05]"}`}>
                <Icon className="h-4 w-4 opacity-80" /><span className="flex-1">{t(label)}</span>
                {badges[to] > 0 && <b className="rounded-full bg-[#FFC708] px-[7px] py-px text-[11px] text-[#1C1C1C]" data-testid={`nav-badge-${slug(label)}`}>{badges[to]}</b>}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

const NAV_PERM = { "/registrations": ["__admin_only"], "/authorities": ["settings"], "/billing": ["billing_admin", "billing_view"], "/templates": ["templates"], "/audit": ["audit"], "/users": ["users_admin"], "/team": [null, "team_manage"], "/calendar": [null, "calendar"], "/documents": [null, "docs_view"], "/requests": ["requests", "requests"], "/requests/new": ["requests", "requests"] };
const allowed = (user, isStaff) => ([path]) => {
  const p = NAV_PERM[path];
  if (!p || user.role === "admin") return true;
  const key = p[isStaff ? 0 : 1];
  return !key || !!user.perms?.[key];
};


function useRegistrationCount(enabled) {
  const [n, setN] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => api.get("/registrations/pending-count").then((r) => setN(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("registrations-changed", load);
    return () => { clearInterval(t); window.removeEventListener("registrations-changed", load); };
  }, [enabled, pathname]);
  return n;
}

const Brand = () => (
  <div className="flex items-center gap-2.5 px-2 pb-3" data-testid="sidebar-brand">
    <img src={BRAND.logo} alt="FILIT" className="h-[34px] w-[34px] shrink-0 object-contain" data-testid="sidebar-logo" />
    <div className="leading-tight" translate="no">
      <div className="font-heading text-[19px] font-semibold text-white">FILIT</div>
      <div className="text-[11px] text-[#A9B7C4]">Compliance Hub</div>
    </div>
  </div>
);

function NotificationBell({ boxed }) {
  const [data, setData] = useState({ items: [], unread: 0 });
  const navigate = useNavigate();
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const open = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    load();
    if (n.link) navigate(n.link);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell-btn" className={boxed ? "relative flex h-10 w-10 items-center justify-center rounded-xl border bg-card shadow-sm hover:border-primary/60" : "relative rounded-md p-2 hover:bg-muted"}>
          <Bell className={boxed ? "h-4 w-4" : "h-5 w-5"} />
          {data.unread > 0 && <span data-testid="notification-unread-count" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <span className="font-heading text-sm font-bold">Notifications</span>
          <button data-testid="notification-read-all-btn" className="text-xs text-primary hover:underline" onClick={() => api.post("/notifications/read-all").then(load)}>Mark all read</button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="p-4 text-sm text-muted-foreground">You're all caught up.</p>}
          {data.items.slice(0, 20).map((n) => (
            <button key={n.id} onClick={() => open(n)} data-testid={`notification-item-${n.id}`} className={`block w-full border-b px-4 py-3 text-left hover:bg-muted ${n.read ? "" : "bg-blue-50/60 dark:bg-sky-500/10"}`}>
              <div className="flex items-center gap-2 text-sm font-semibold">{n.critical && <ShieldCheck className="h-3.5 w-3.5 text-red-600" />}{n.title}</div>
              {n.body && <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{n.body}</div>}
              <div className="mt-1 text-[11px] text-muted-foreground">{fmtDateTime(n.created_at)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Shell() {
  const { user, logout, isStaff } = useAuth();
  const { t, lang } = useLang();
  const [menu, setMenu] = useState(false);
  const regCount = useRegistrationCount(user.role === "admin" || !!user.perms?.signups_review);
  const [dark, toggleTheme] = useTheme();
  useEffect(() => {
    if (sessionStorage.getItem("company_reset")) {
      sessionStorage.removeItem("company_reset");
      toast.warning("You no longer have access to the company you had selected, so we switched you to one you can access.");
    }
  }, []);
  if (!isStaff) return <ClientShell bell={<NotificationBell boxed />} allowed={allowed(user, false)} />;
  const bottom = NAVC.flatMap(([, i]) => i).filter(staffOk(user)).filter(([to]) => ["/", "/waiting", "/quotes", "/tasks", "/clients"].includes(to));
  const sidebar = (
    <div className="flex h-full flex-col overflow-hidden bg-[#0E151C] text-[#B8C4CF]">
      <div className="px-2 pt-[18px]"><Brand /></div>
      <div className="flex-1 overflow-y-auto px-[10px] py-[6px]"><StaffNav user={user} onClick={() => setMenu(false)} badges={{ "/clients": regCount }} /></div>
      <NavLink to="/account" onClick={() => setMenu(false)} className="m-[10px] block rounded-[12px] bg-white/[.06] px-[14px] py-3 text-white" data-testid="sidebar-user-card">
        <b className="block truncate text-sm" data-testid="sidebar-user-name">{user.name}</b>
        <small className="block text-xs text-[#8a98a6]">{ROLE_LABEL[user.role]} · Account</small>
      </NavLink>
    </div>
  );
  return (
    <div className="min-h-screen">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[244px] lg:block">{sidebar}</aside>
      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side="left" className="w-[270px] border-0 p-0"><SheetTitle className="sr-only">Menu</SheetTitle>{sidebar}</SheetContent>
      </Sheet>
      <div className="lg:pl-[244px]">
        <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b bg-card px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <button className="rounded-[10px] p-2 hover:bg-muted lg:hidden" onClick={() => setMenu(true)} data-testid="mobile-menu-btn" aria-label="Open menu"><Menu className="h-5 w-5" /></button>
            <span className="hidden text-[14px] font-semibold text-muted-foreground sm:inline" data-testid="header-context">{t("V.A Staff Workspace")}</span>
          </div>
          <div className="flex items-center gap-2">
            <small className="hidden text-[12.5px] text-[#7b8794] sm:inline" data-testid="header-today">{new Date().toLocaleDateString(lang === "sw" ? "sw-TZ" : "en-GB", { day: "numeric", month: "short", year: "numeric" })}</small>
            <LanguageSwitch />
            <button onClick={toggleTheme} aria-label="Toggle theme" title={dark ? "Light mode" : "Dark mode"} data-testid="theme-toggle-btn" className="flex h-10 w-10 items-center justify-center rounded-[10px] border bg-card font-semibold shadow-sm hover:border-primary/60">◐</button>
            <NotificationBell boxed />
            <button data-testid="logout-btn" onClick={logout} className="flex h-10 items-center gap-1.5 rounded-[10px] border bg-card px-3 text-sm font-semibold shadow-sm hover:border-primary/60"><LogOut className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t("Sign out")}</span></button>
          </div>
        </header>
        <main className="mx-auto max-w-[1400px] p-4 pb-24 sm:p-6 lg:px-8 lg:pb-10 lg:pt-4"><CommercialBanner /><Outlet />
          <footer className="mt-10 text-center text-[11.5px] text-muted-foreground" translate="no" data-testid="staff-footer"><p data-testid="staff-copyright">{BRAND.copyright}</p></footer></main>
      </div>
      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 bg-[#0F1720] pb-[env(safe-area-inset-bottom)] dark:bg-[#080D12] lg:hidden">
        {bottom.map(([to, label, Icon]) => (
          <NavLink key={to} to={to} end={to === "/"} data-testid={`bottom-nav-${slug(label)}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 text-[10.5px] font-medium ${isActive ? "text-[#38BDF8]" : "text-[#A9B7C4]"}`}>
            <Icon className="h-5 w-5" /><span className="truncate px-1">{t(label === "Waiting on client" ? "Waiting" : label)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
